import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, message, Modal, Typography } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { buildVoucherColumns } from './voucherColumns';
import { voucherApi } from './voucherApi';
import JournalVoucherModal from './JournalVoucherModal';
// Stage 6B.9F — period guard for the Post action.
import { accountsApi } from '../accountsApi';
import {
  classifyPeriodForDate, periodGuardError, loadPeriodsForGuard
} from '../accountingPeriodGuard';
import '../accounts.css';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Journal Voucher page (Stage 6B.4).
//
// Grid + Add/Edit/View modal + right-click context menu (View / Edit /
// Post / Cancel / Delete). Approval-MFA wired for Post + Cancel via
// useApprovalMfa hook (challenge → verify → action → consume).
//
// Modal sequencing follows the Customer Master pattern:
//   - confirm Modal → resolves only on afterClose
//   - then runWithApproval opens the MFA modal cleanly
// so two modals never stack visually.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = { list: (query) => voucherApi.list(query) };

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load vouchers.';
  message.error({ content: msg, key: 'voucher-list' });
}

export default function JournalVoucherPage() {
  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildVoucherColumns({ currentPage, pageSize }),
    [currentPage, pageSize]);

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [drawerRecord, setDrawerRecord] = useState(null);

  // Stage 6B.9F — period guard. Fetch periods on mount so Post action
  // can pre-check before round-trip. Refresh on refetch too so a
  // freshly-locked period kicks in without a page reload.
  const [periods, setPeriods] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadPeriodsForGuard(accountsApi).then((out) => {
      if (cancelled) return;
      setPeriods((out && out.rows) || []);
    });
    return () => { cancelled = true; };
  }, []);

  const openAdd = useCallback(() => {
    setDrawerMode('add'); setDrawerRecord(null); setDrawerOpen(true);
  }, []);
  const openWithFetch = useCallback(async (record, mode) => {
    try {
      const full = await voucherApi.get(record.id);
      setDrawerMode(mode);
      setDrawerRecord(full);
      setDrawerOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load voucher.');
    }
  }, []);
  const openEdit = useCallback((record) => openWithFetch(record, 'edit'), [openWithFetch]);
  const openView = useCallback((record) => openWithFetch(record, 'view'), [openWithFetch]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const onSaved = useCallback(async () => {
    setDrawerOpen(false);
    await refetch();
  }, [refetch]);

  // ── POST flow: confirm → Approval MFA → run ──
  const handlePost = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft vouchers can be posted (this one is ${record.status}).`);
      return;
    }
    // Stage 6B.9F — period guard (client-side pre-check).
    const cls = classifyPeriodForDate(periods, record.voucherDate);
    const guard = periodGuardError(cls);
    if (guard) {
      message.error(guard.message);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Post voucher ${record.voucherNo}?`,
        icon: <CheckCircleOutlined style={{ color: '#3f8600' }} />,
        content: 'Posting will update ledger balances and lock the voucher row. You will be asked for a 6-digit Approval MFA code to confirm.',
        okText: 'Post',
        cancelText: 'Cancel',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   'accounts.voucher.post',
        resourceType:   'accounting_voucher',
        resourceId:     record.id,
        operationLabel: 'Post journal voucher',
        resourceLabel:  `${record.voucherNo} · ${record.voucherDate ? String(record.voucherDate).slice(0, 10) : ''}`,
        action: (approvalMfaChallengeId) =>
          voucherApi.post(record.id, { approvalMfaChallengeId })
      });
      message.success('Voucher posted.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(err?.response?.data?.error?.message || 'Could not post the voucher. Please check the form and try again.');
    }
  }, [refetch, runWithApproval, periods]);

  // ── CANCEL flow: reason prompt → confirm → Approval MFA → run ──
  const handleCancel = useCallback(async (record) => {
    if (record.status !== 'posted') {
      message.warning(`Only posted vouchers can be cancelled (this one is ${record.status}).`);
      return;
    }
    // Prompt for reason (must be 3+ chars; backend enforces too).
    const reason = await new Promise((resolve) => {
      let value = '';
      let okClicked = false;
      const ReasonForm = () => (
        <div>
          <p style={{ marginTop: 0 }}>
            Cancelling will REVERSE the ledger balance impact of this posted voucher.
            Provide a short reason (audit trail).
          </p>
          <Form layout="vertical">
            <Form.Item label="Reason" required>
              <Input.TextArea rows={3} maxLength={500}
                onChange={(e) => { value = e.target.value; }} />
            </Form.Item>
          </Form>
        </div>
      );
      Modal.confirm({
        title: `Cancel voucher ${record.voucherNo}?`,
        icon: <ExclamationCircleOutlined />,
        content: <ReasonForm />,
        okText: 'Continue',
        okButtonProps: { danger: true },
        cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked ? value : null)
      });
    });
    if (!reason) return;
    if (String(reason).trim().length < 3) {
      message.error('Reason must be at least 3 characters.');
      return;
    }
    try {
      await runWithApproval({
        operationKey:   'accounts.voucher.cancel',
        resourceType:   'accounting_voucher',
        resourceId:     record.id,
        operationLabel: 'Cancel journal voucher',
        resourceLabel:  `${record.voucherNo}`,
        action: (approvalMfaChallengeId) =>
          voucherApi.cancel(record.id, { reason: String(reason).trim() }, { approvalMfaChallengeId })
      });
      message.success('Voucher cancelled.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(err?.response?.data?.error?.message || 'Failed to cancel voucher.');
    }
  }, [refetch, runWithApproval]);

  const handleDelete = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft vouchers can be deleted. This voucher is already ${record.status} — use Cancel instead.`);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete draft ${record.voucherNo}?`,
        icon:  <ExclamationCircleOutlined />,
        content: 'The draft will be soft-deleted. You will be asked for a 6-digit Approval MFA code to confirm.',
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      // Stage 6B.9F — Approval-MFA L1 for draft delete (matching the
      // 6B.9C Contra pattern).
      await runWithApproval({
        operationKey:   'accounts.voucher.delete',
        resourceType:   'accounting_voucher',
        resourceId:     record.id,
        operationLabel: 'Delete draft journal voucher',
        resourceLabel:  `${record.voucherNo}`,
        action: (approvalMfaChallengeId) =>
          voucherApi.remove(record.id, { approvalMfaChallengeId })
      });
      message.success('Draft deleted.');
      await refetch();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      message.error(e?.response?.data?.error?.message || 'Could not delete the draft. Please refresh and try again.');
    }
  }, [refetch, runWithApproval]);

  const actionsProp     = useMemo(() => ({ add: true, view: true, edit: true, delete: false }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'view',   label: 'View',         icon: <EyeOutlined />,            onClick: openView },
    { key: 'edit',   label: 'Edit Draft',   icon: <EditOutlined />,           onClick: openEdit },
    { key: 'post',   label: 'Post',         icon: <CheckCircleOutlined />,    onClick: handlePost },
    { key: 'cancel', label: 'Cancel',       icon: <CloseCircleOutlined />,    danger: true, onClick: handleCancel },
    { key: 'delete', label: 'Delete Draft', icon: <DeleteOutlined />,         danger: true, onClick: handleDelete }
  ]), [openView, openEdit, handlePost, handleCancel, handleDelete]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <ErpDataGrid
        title="Journal Vouchers"
        gridId="accounts-vouchers-journal-v1"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={56}
        pageSizeOptions={pageSizeOptions}
        searchable
        searchPlaceholder="Search by voucher no / narration…"
        searchShortcut
        exportable
        exportFilename="journal-vouchers"
        actions={actionsProp}
        addButtonText="Add Journal Voucher"
        emptyTitle="No vouchers yet"
        emptyText="Click Add Journal Voucher to record your first journal entry."
        emptyCtaLabel="Add Journal Voucher"
        onAdd={openAdd}
        onEdit={openEdit}
        onView={openView}
        onRowClick={openView}
        contextMenuActions={contextMenuActions}
        {...gridHandlers}
      />
      <JournalVoucherModal
        open={drawerOpen}
        mode={drawerMode}
        record={drawerRecord}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
      {ApprovalMfaModalSlot}
      </div>
    </ErpMasterShell>
  );
}
